import type { FC, ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import axios from 'src/utils/axios';
import CsvGrid from 'src/components/SupportCase/CsvGrid';
import HtmlPreviewFrame from 'src/components/SupportCase/HtmlPreviewFrame';
import {
  fileKindLabel,
  formatFileSize,
} from 'src/constants/supportCaseAttachments';
import type { SupportDocument } from 'src/components/SupportCase/types';

/**
 * One dialog for every artifact the Assistant hands over (PA-11).
 *
 * Dispatch is by the *sniffed* content type Thor stored, not by the extension:
 *
 * - **HTML** — the sandboxed frame (`HtmlPreviewFrame`).
 * - **PDF** — the browser's own viewer, streaming the inline preview endpoint.
 * - **CSV** — the grid (`CsvGrid`), fetched once and parsed here.
 * - **XLSX / anything else** — download only (decision D6): a spreadsheet is opened in
 *   Excel, and re-implementing a workbook renderer to pretend otherwise helps nobody.
 *
 * Dialog shape follows `pronto-dialog`: Cancel-left, primary-right, title is
 * `<Verb> <Noun>`. The only action is a download (a file action, not a compliance
 * action), so it is the primary and it sits on the right.
 */
const CSV_TYPES = ['text/csv', 'application/csv'];
const HTML_TYPES = ['text/html'];
const PDF_TYPES = ['application/pdf'];

const isType = (contentType: string, allowed: string[]): boolean =>
  allowed.some((entry) => contentType.toLowerCase().includes(entry));

/**
 * Two spellings of the same endpoint, and they are not interchangeable.
 *
 * `downloadUrl` is what the **browser** navigates to (`<a href>`, `window.open`), so it
 * carries the `/api` prefix. `apiPath` is what **axios** is given, and our instance's
 * baseURL already ends in `/api/` — passing the prefixed form there requested
 * `/api/api/support/…` and the CSV preview failed with "This file could not be opened"
 * while the HTML frame (which posts a relative path) worked. Found in the acceptance run
 * on dev; the unit test had mocked axios and so asserted the bug instead of catching it.
 */
const downloadUrl = (document: SupportDocument): string =>
  `/api/support/documents/${document.id}/download/`;

/** BaseURL-relative: axios prepends `/api/`. */
const apiPath = (document: SupportDocument): string =>
  `/support/documents/${document.id}/download/`;

interface FilePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  document: SupportDocument | null;
}

const FilePreviewDialog: FC<FilePreviewDialogProps> = ({
  open,
  onClose,
  document,
}) => {
  const [csvText, setCsvText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const contentType = document?.content_type ?? '';

  useEffect(() => {
    setCsvText(null);
    setError(null);

    if (!open || !document || !isType(contentType, CSV_TYPES)) {
      return undefined;
    }

    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const response = await axios.get<string>(apiPath(document), {
          responseType: 'text',
          transformResponse: [(data) => data],
        });
        if (!cancelled) {
          setCsvText(
            typeof response.data === 'string'
              ? response.data
              : String(response.data)
          );
        }
      } catch {
        if (!cancelled) {
          setError('This file could not be opened. Download it instead.');
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, document, contentType]);

  const handleDownload = useCallback(() => {
    if (!document) {
      return;
    }
    // A real navigation, so the browser's own download UI handles it.
    window.open(downloadUrl(document), '_blank', 'noopener,noreferrer');
  }, [document]);

  if (!document) {
    return null;
  }

  const renderBody = (): ReactElement => {
    if (isType(contentType, HTML_TYPES)) {
      return (
        <HtmlPreviewFrame
          documentId={document.id}
          title={`Preview of ${document.name}`}
        />
      );
    }
    if (isType(contentType, PDF_TYPES)) {
      return (
        <Box
          component="iframe"
          title={`Preview of ${document.name}`}
          src={`/api/support/documents/${document.id}/preview/`}
          data-testid="pdf-preview-frame"
          sx={{ width: '100%', height: '70vh', border: 0 }}
        />
      );
    }
    if (isType(contentType, CSV_TYPES)) {
      if (error) {
        return <Alert severity="error">{error}</Alert>;
      }
      if (csvText === null) {
        return (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={24} />
          </Stack>
        );
      }
      return <CsvGrid text={csvText} />;
    }
    return (
      <Alert severity="info">
        {fileKindLabel(document.name)} files open in Excel. Download it to
        review the rows.
      </Alert>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="h6" component="span" noWrap sx={{ minWidth: 0 }}>
            View {document.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {fileKindLabel(document.name)} · {formatFileSize(document.size)}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>{renderBody()}</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleDownload}>
          Download file
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilePreviewDialog;
